import { useState, useEffect, useMemo } from "react";
import { Modal } from "react-bootstrap";
import { translate } from "@/modules/shared/IntlProviderWrapper";
import ReactTable, { type ReactTableColumn } from "@/modules/shared/ui/ReactTable";
import MultiSelect from "@/modules/shared/ui/forms/MultiSelect";
import { stringCompare } from "@/lib/util";
import type { Row, Column } from "react-table";

export type PersonLineItem = {
    person_name: string;
    person_external_id: string;
    line_title: string;
    line_external_id: string;
    score: number;
    dwell: number;
    warmup: number;
};

type Props = {
    show: boolean;
    title?: string;
    getPersonLineItems: () => PersonLineItem[];
    onHide: () => void;
};

type FilterProps = {
    filterValue?: string[];
    values: string[];
    id: string;
    setFilter: (column_id: string, value?: string[]) => void;
    placeholder?: string;
    getLabel?: (value: string) => string;
};

type SelectedOption = {
    value: string;
    label: string;
};

const MultiSelectFilter = (props: FilterProps) => {
    const [selectedValues, setSelectedValues] = useState<string[]>(() => props.filterValue ?? []);

    // Sync with external filterValue changes
    useEffect(() => {
        setSelectedValues(props.filterValue ?? []);
    }, [props.filterValue]);

    // ["value1", "value2"] -> [{ value: "value1", label: "value1" }, ...]
    const options = props.values.map((value: string) => ({
        value,
        label: props.getLabel ? props.getLabel(value) : value,
    }));

    const selectedOptions = selectedValues
        .map(value => options.find(opt => opt.value === value))
        .filter(Boolean) as SelectedOption[];

    const onChange = (selectedOptions: SelectedOption[] | null) => {
        const newSelectedValues = selectedOptions ? selectedOptions.map(opt => opt.value) : [];
        setSelectedValues(newSelectedValues);
        props.setFilter(props.id, newSelectedValues);
    };

    return (
        <MultiSelect
            options={options}
            value={selectedOptions}
            onChange={onChange}
            placeholder={props.placeholder || translate("common.filter", "Filter")}
            isClearable
            show_count={true}
            fullWidth={true}
        />
    );
};

const filterMultiSelect = (rows: Row[], id: string[], filterValues: string[]): Row[] => {
    if (filterValues.length === 0) {
        return rows;
    }
    return rows.filter(row => {
        const rowValue = row.values[id[0]] as string;
        return filterValues.includes(rowValue);
    });
};

const sortString = (rowA: Row<PersonLineItem>, rowB: Row<PersonLineItem>, columnId: string): number => {
    const a = String(rowA.original[columnId as keyof PersonLineItem]);
    const b = String(rowB.original[columnId as keyof PersonLineItem]);
    return stringCompare(a, b);
};

export const PersonLineModal = ({ show, title, getPersonLineItems, onHide }: Props) => {
    const [items, setItems] = useState<PersonLineItem[]>([]);

    useEffect(() => {
        if (show) {
            setItems(getPersonLineItems());
        }
    }, [show, getPersonLineItems]);

    const uniquePersonNames = useMemo(() => {
        const uniqueNames = new Set(items.map(item => item.person_name));
        return Array.from(uniqueNames).sort(stringCompare);
    }, [items]);

    const uniqueLineTitles = useMemo(() => {
        const uniqueTitles = new Set(items.map(item => item.line_title));
        return Array.from(uniqueTitles).sort(stringCompare);
    }, [items]);

    const getColumns = (): ReactTableColumn<PersonLineItem>[] => {
        return [
            {
                Header: "",
                accessor: "person_name",
                enableSorting: true,
                sortType: sortString,
                filter: filterMultiSelect,
                Filter: (props: Column<PersonLineItem>) => (
                    <MultiSelectFilter
                        values={uniquePersonNames}
                        id={props.column.id}
                        setFilter={props.setFilter}
                        filterValue={props.column.filterValue}
                        placeholder={translate("PersonLineModal.person_name", "Person name")}
                    />
                ),
                Cell: ({ row }) => {
                    const item = row.original;
                    return (
                        <div className="nowrap">
                            {item.person_name}{" "}
                            <span className="text-smaller text-muted">{item.person_external_id}</span>
                        </div>
                    );
                },
            },
            {
                Header: "",
                accessor: "line_title",
                enableSorting: true,
                sortType: sortString,
                filter: filterMultiSelect,
                Filter: (props: Column<PersonLineItem>) => (
                    <MultiSelectFilter
                        values={uniqueLineTitles}
                        id={props.column.id}
                        setFilter={props.setFilter}
                        filterValue={props.column.filterValue}
                        placeholder={translate("PersonLineModal.line_title", "Line title")}
                    />
                ),
                Cell: ({ row }) => {
                    const item = row.original;
                    return (
                        <div className="nowrap">
                            {item.line_title} <span className="text-smaller text-muted">{item.line_external_id}</span>
                        </div>
                    );
                },
            },
            {
                Header: "",
                accessor: "score",
                enableSorting: true,
                filterInputPlaceholder: translate("PersonLineModal.score", "Score"),
                textAlign: "right",
            },
            {
                Header: "",
                accessor: "dwell",
                enableSorting: true,
                filterInputPlaceholder: translate("PersonLineModal.dwell", "Dwell"),
                textAlign: "right",
            },
            {
                Header: "",
                accessor: "warmup",
                enableSorting: true,
                filterInputPlaceholder: translate("PersonLineModal.warmup", "Warmup"),
                textAlign: "right",
            },
        ];
    };

    return (
        <Modal dialogClassName="modal-dialog-scrollable modal-max h-100" show={show} onHide={onHide} size="lg">
            <Modal.Header>
                <Modal.Title>{title || translate("PersonLineModal.title", "Person Line Scores")}</Modal.Title>
                <button type="button" className="close" onClick={onHide}>
                    <span aria-hidden="true">×</span>
                    <span className="sr-only">{translate("common.close", "Close")}</span>
                </button>
            </Modal.Header>
            <Modal.Body className="p-0">
                <div className="p-3">
                    <ReactTable data={items} columns={getColumns()} />
                </div>
            </Modal.Body>
        </Modal>
    );
};

export default PersonLineModal;
